import { Fragment, useEffect, useMemo } from "react"
import { useInView } from "react-intersection-observer"

import { marketingClient } from "@trueskin-web/apis"
import {
  Container,
  MobileStickyCta,
  Seo,
  Spinner,
} from "@trueskin-web/components"
import { getCountry } from "@trueskin-web/locales"
import { useIsScreenSizeSmall } from "@trueskin-web/theme"
import { Trans, useTranslation } from "@trueskin-web/translations"

import TrustedShopsBadge from "../trusted-shops-badge"

import FAQs from "./components/faqs"
import MoneyBack from "./components/money-back"
import PageHeader from "./components/page-header"
import Plans from "./components/plans/plans"
import Products from "./components/products"
import Testimonials from "./components/testimonials"
import withPresets from "./components/with-presets"

export const ChoosePlan = ({
  plans,
  selectedPlanId,
  isLoading,
  isSubmitting,
  error,
  onSelect,
  onSubmit,
}) => {
  const country = getCountry()
  const { t } = useTranslation()
  const [inViewRef, isInView] = useInView()
  const isMobile = useIsScreenSizeSmall()

  const selectedPlan = useMemo(() => {
    return plans
      .flatMap((plan) => plan.variants)
      .find((variant) => variant.id === selectedPlanId)
  }, [plans, selectedPlanId])

  useEffect(() => {
    if (!isLoading && !error) {
      marketingClient.sendEvent({
        name: marketingClient.EVENT_TYPES_ENUM.PRODUCT_SELECTION_PAGE_OPENED,
      })
    }
  }, [isLoading, error])

  if (isLoading || isSubmitting) {
    return <Spinner isFullPage />
  }

  if (error) {
    return (
      <span>
        <Trans i18Key="ChoosePlan.errorMessage" message={error.message} />
      </span>
    )
  }

  return (
    <Fragment>
      <Seo title={t("ChoosePlan.seoTitle")} />

      <PageHeader />
      <Products />

      <div ref={inViewRef} sx={{ mx: "-16px", mb: 10 }}>
        <Plans
          plans={plans}
          selectedPlanId={selectedPlanId}
          onSelect={onSelect}
          onSubmit={onSubmit}
        />
        <Container>
          <div
            sx={{
              display: ["block", "grid"],
              mt: 5,
              gridTemplateColumns: [null, "1fr 2fr"],
              gridColumnGap: [null, 6],
            }}
          >
            <MoneyBack />
            <Testimonials />
          </div>
          {country === "DE" && <FAQs />}
        </Container>
      </div>

      {isMobile && selectedPlan && (
        <MobileStickyCta
          dataTestId="web-portal-plan-selection-mobile-cta"
          show={isInView}
          title={t("ChoosePlan.continueButton", {
            title: selectedPlan.isSubscription
              ? "Auto-Refill"
              : selectedPlan.title,
          })}
          onClick={() => onSubmit(selectedPlanId)}
        />
      )}

      <TrustedShopsBadge />
    </Fragment>
  )
}

export default withPresets(ChoosePlan)
